/**
 * `SecretKey` — enveloppe de matériel de clé.
 *
 * Objectif de sécurité : rendre une fuite ACCIDENTELLE impossible.
 * `console.log`, `JSON.stringify`, l'interpolation de chaîne, `util.inspect`
 * et les rapports de crash affichent tous `[redacted]`. Lire les octets exige
 * un appel EXPLICITE à `.reveal()` — facile à auditer par recherche textuelle.
 *
 * Voir `docs/ARCHITECTURE.md` §6 invariants 1 et 4.
 */
import { assertBytes, constantTimeEqual, wipe } from './bytes.js';
import { InvalidParameterError, SecretDestroyedError } from './errors.js';
import { randomBytes } from './random.js';

/** Étiquette non secrète, utile aux diagnostics. */
export type SecretLabel =
  | 'masterKey'
  | 'stretchedMasterKey'
  | 'vaultKey'
  | 'symmetricKey'
  | 'recoveryKey'
  | 'recoveryStretchedKey'
  | 'passkeyPrfOutput'
  | 'passkeyStretchedKey'
  | 'sendSecret'
  | 'sendKey'
  | 'emergencyKit'
  | 'emergencySealingKey'
  | 'serviceTokenSecret'
  | 'serviceTokenKey';

const REDACTED = '[redacted]';

export class SecretKey {
  /** Étiquette d'usage (jamais secrète). */
  readonly label: SecretLabel;
  #bytes: Uint8Array | null;
  readonly #length: number;

  private constructor(bytes: Uint8Array, label: SecretLabel) {
    this.#bytes = bytes;
    this.#length = bytes.length;
    this.label = label;
  }

  /**
   * Adopte un tampon existant **sans le copier** : l'appelant ne doit plus s'en
   * servir. Utiliser `SecretKey.fromCopy` en cas de doute sur la propriété.
   */
  static adopt(bytes: Uint8Array, label: SecretLabel, expectedLength?: number): SecretKey {
    assertBytes(bytes, `secret:${label}`, expectedLength);
    if (bytes.length === 0) {
      throw new InvalidParameterError(`secret:${label}: cle vide refusee`);
    }
    return new SecretKey(bytes, label);
  }

  /** Copie défensive du tampon fourni. */
  static fromCopy(bytes: Uint8Array, label: SecretLabel, expectedLength?: number): SecretKey {
    assertBytes(bytes, `secret:${label}`, expectedLength);
    return SecretKey.adopt(Uint8Array.from(bytes), label, expectedLength);
  }

  /** Génère une clé aléatoire via le CSPRNG de la plateforme. */
  static random(length: number, label: SecretLabel): SecretKey {
    return SecretKey.adopt(randomBytes(length), label);
  }

  /** Longueur en octets (métadonnée non secrète). */
  get byteLength(): number {
    return this.#length;
  }

  /** `true` si `destroy()` a déjà été appelé. */
  get destroyed(): boolean {
    return this.#bytes === null;
  }

  /**
   * Accès EXPLICITE aux octets. Renvoie la vue interne (pas une copie) pour
   * éviter de multiplier les copies de clé en mémoire : **ne pas muter**.
   */
  reveal(): Uint8Array {
    if (this.#bytes === null) throw new SecretDestroyedError(this.label);
    return this.#bytes;
  }

  /** Copie des octets, à la charge de l'appelant de l'effacer. */
  revealCopy(): Uint8Array {
    return Uint8Array.from(this.reveal());
  }

  /** Nouvelle enveloppe indépendante portant le même matériel. */
  clone(label: SecretLabel = this.label): SecretKey {
    return SecretKey.adopt(this.revealCopy(), label);
  }

  /** Égalité à temps constant, sans exposer les octets. */
  equals(other: SecretKey): boolean {
    if (!(other instanceof SecretKey)) return false;
    if (this.destroyed || other.destroyed) return false;
    return constantTimeEqual(this.reveal(), other.reveal());
  }

  /**
   * Écrase le tampon et invalide l'enveloppe (idempotent).
   * L'effacement mémoire n'est pas garanti en JS : voir README §« Limites ».
   */
  destroy(): void {
    if (this.#bytes !== null) {
      wipe(this.#bytes);
      this.#bytes = null;
    }
  }

  // --- Barrières anti-fuite -------------------------------------------------

  toString(): string {
    return `SecretKey(${this.label}, ${this.#length}o, ${REDACTED})`;
  }

  toJSON(): string {
    return REDACTED;
  }

  get [Symbol.toStringTag](): string {
    return 'SecretKey';
  }

  /** `util.inspect` / `console.log` sous Node. */
  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return this.toString();
  }
}

/** Type-guard exporté pour les couches applicatives. */
export function isSecretKey(value: unknown): value is SecretKey {
  return value instanceof SecretKey;
}

export function assertSecretKey(
  value: unknown,
  name: string,
  expectedLength?: number,
): asserts value is SecretKey {
  if (!isSecretKey(value)) {
    throw new InvalidParameterError(`${name}: SecretKey attendue`);
  }
  if (value.destroyed) {
    throw new SecretDestroyedError(value.label);
  }
  if (expectedLength !== undefined && value.byteLength !== expectedLength) {
    throw new InvalidParameterError(
      `${name}: cle de ${expectedLength} octets attendue, ${value.byteLength} recus`,
    );
  }
}
